import React, { createContext, useContext, useState, useCallback } from 'react';

export type CancelBehaviorType = 'stay' | 'history_or_home_with_gate_check';

export interface CancelBehavior {
  type: CancelBehaviorType;
}

interface AuthModalConfig {
  afterAuthRedirect?: string;
  reason?: string;
  fromPath?: string; // The pathname where the modal was opened from
  onCancelBehavior?: CancelBehavior; // How to handle cancel - defaults to 'history_or_home_with_gate_check'
  postAuthAction?: () => void; // Callback to execute after successful authentication
  forceAuth?: boolean; // If true, show modal even if already authenticated (for forced auth flows)
}

interface AuthModalContextType {
  isOpen: boolean;
  openAuthModal: (config?: AuthModalConfig) => void;
  closeAuthModal: () => void;
  config: AuthModalConfig | null;
}

const AuthModalContext = createContext<AuthModalContextType | undefined>(undefined);

export function AuthModalProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [config, setConfig] = useState<AuthModalConfig | null>(null);

  const openAuthModal = useCallback((newConfig?: AuthModalConfig) => {
    setConfig(newConfig || null);
    setIsOpen(true);
  }, []);

  const closeAuthModal = useCallback(() => {
    setIsOpen(false);
    // Clear config after a short delay to allow navigation to complete
    // Navigation is handled in LoginFlowModal after auth success
    setTimeout(() => {
      setConfig(null);
    }, 100);
  }, []);

  return (
    <AuthModalContext.Provider value={{ isOpen, openAuthModal, closeAuthModal, config }}>
      {children}
    </AuthModalContext.Provider>
  );
}

export function useAuthModal() {
  const context = useContext(AuthModalContext);
  if (context === undefined) {
    throw new Error('useAuthModal must be used within an AuthModalProvider');
  }
  return context;
}

